import { ReactNode } from "react"
import { defineBlock } from "eddev/blocks"
import { twMerge } from "tailwind-merge"
import { ToolCard } from "@components/atoms/tools/ToolCard"
import { FatigueDetectionAnimation } from "@components/atoms/tools/FatigueDetectionAnimation"
import { DistractionAssessmentAnimation } from "@components/atoms/tools/DistractionAssessmentAnimation"
import { DistractionPolicyAnimation } from "@components/atoms/tools/DistractionPolicyAnimation"
import { ManageFatigueAnimation } from "@components/atoms/tools/ManageFatigueAnimation"
import { DriveInTheMomentAnimation } from "@components/atoms/tools/DriveInTheMomentAnimation"
import { useAppData } from "eddev/hooks"
import { CommonDataQuery } from "../../types.graphql"

export const meta: BlockMeta = {
  title: "Tools Container",
  category: "common",
  icon: "car",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post"],
  },
}

// The ACF `tool1`/`tool2`/`tool3` select fields return plain strings whose
// choices are hand-kept in sync with `themeSiteSettings`'s keys, so we type
// and validate them against the actual GraphQL shape rather than trusting ACF.
type ThemeSiteSettings = NonNullable<NonNullable<CommonDataQuery["siteSettings"]>["themeSiteSettings"]>
type ToolKey = keyof ThemeSiteSettings

const TOOLS: { key: ToolKey; animation: ReactNode }[] = [
  {
    key: "fatigueDetection",
    animation: <FatigueDetectionAnimation />,
  },
  {
    key: "distractionAssessment",
    animation: <DistractionAssessmentAnimation />,
  },
  {
    key: "distractionPolicy",
    animation: <DistractionPolicyAnimation />,
  },
  {
    key: "fatigueManagement",
    animation: <ManageFatigueAnimation />,
  },
  {
    key: "driveInTheMoment",
    animation: <DriveInTheMomentAnimation />,
  },
]

const TOOL_KEYS = new Set<string>(TOOLS.map((tool) => tool.key))
const isToolKey = (value: string | null | undefined): value is ToolKey => !!value && TOOL_KEYS.has(value)

export default defineBlock("rsrh/tools-container", (props) => {
  const toolData = useAppData((data) => data.siteSettings?.themeSiteSettings)

  if (props.style) {
    const tool = isToolKey(props.tool1) ? TOOLS.find((tool) => tool.key === props.tool1) : undefined
    const data = tool && toolData?.[tool.key]

    return (
      <div className="grid-auto gap-6 py-6">
        {tool && data && (
          <div className="col-span-12">
            <ToolCard {...data} animation={tool.animation} large />
          </div>
        )}
      </div>
    )
  }

  const visibleTools = [props.tool1, props.tool2, props.tool3]
    .filter(isToolKey)
    .map((toolKey) => TOOLS.find((tool) => tool.key === toolKey))
    .filter((tool): tool is (typeof TOOLS)[number] => Boolean(tool))

  return (
    <div className="grid-auto gap-6 py-6">
      {visibleTools.map((tool, index) => {
        const data = toolData?.[tool.key]

        return (
          <div
            key={tool.key}
            className={twMerge(
              "col-span-12 md:col-span-4",
              index === 0 && visibleTools.length === 2 && "md:col-start-3",
              index === 0 && visibleTools.length === 1 && "md:col-start-4",
            )}
          >
            {data && <ToolCard {...data} animation={tool.animation} />}
          </div>
        )
      })}
    </div>
  )
})
